/**
 * The practice bot: a utility-scored state machine (approach, strafe, retreat, cover, hold) with
 * reaction-delayed perception, line-of-sight checks, wall avoidance, aim lag and noise, heat
 * discipline, and ability use keyed by each ability's `botHint`. It reads only the previous
 * match state and returns the same input packet a human sends, so it cannot cheat on stats.
 */
import { concealmentAt, pointInYawBox } from '../arena/features.js';
import { SIM_DT } from '../constants.js';
import { weaponOf } from '../combat/catalog.js';
import { getAbility } from '../data/index.js';
import { aimDirection, angleBetween, clamp, degToRad, forwardXZ, pitchOf, rightXZ, vLerp, wrapAngle, yawOf, } from '../sim/math.js';
import { rngBell, rngChance, rngInt } from '../sim/rng.js';
import { IDLE_INPUT } from '../sim/step-mech.js';
import { aimOrigin, centerOf, createBotState } from '../combat/state.js';
import { otherSlot, } from '../combat/types.js';
import { BOT_PROFILES } from './difficulty.js';
function cloneBot(b) {
    return {
        ...b,
        perceived: b.perceived ? { ...b.perceived } : null,
        coverTarget: b.coverTarget ? { ...b.coverTarget } : null,
        lastPosition: { ...b.lastPosition },
    };
}
/** True when nothing in the arena lies between `from` and a target of radius `radius` at `to`. */
export function lineOfSight(world, from, to, radius) {
    const dx = to.x - from.x;
    const dy = to.y - from.y;
    const dz = to.z - from.z;
    const dist = Math.hypot(dx, dy, dz);
    if (dist < 1e-6)
        return true;
    const hit = world.castRayHit(from, { x: dx / dist, y: dy / dist, z: dz / dist }, dist);
    return !hit || hit.distance >= dist - radius;
}
/** Nearest cover point (within 30 m) from which the enemy is not visible. */
export function chooseCover(points, from, enemyCenter, enemyRadius, eyeHeight, world) {
    const ranked = points
        .map((p) => ({ p, d: Math.hypot(p.x - from.x, p.z - from.z) }))
        .filter((c) => c.d <= 30 && c.d > 1)
        .sort((a, b) => a.d - b.d || a.p.x - b.p.x || a.p.z - b.p.z);
    let checks = 0;
    for (const c of ranked) {
        if (checks++ >= 12)
            break;
        const eye = { x: c.p.x, y: c.p.y + eyeHeight, z: c.p.z };
        if (!lineOfSight(world, eye, enemyCenter, enemyRadius))
            return { ...c.p };
    }
    return null;
}
function isCloaked(c) {
    let cloaked = false;
    let revealDistance = 6;
    let revealed = false;
    for (const e of c.effects) {
        if (e.params.cloak) {
            cloaked = true;
            revealDistance = e.params.cloak.revealDistance;
        }
        if (e.params.flags?.includes('revealed'))
            revealed = true;
    }
    return { cloaked: cloaked && !revealed, revealDistance };
}
function optimalRange(weapon) {
    return weapon.kind === 'melee' ? (weapon.melee?.reach ?? 3) * 0.8 : weapon.range.optimal;
}
function wantsAbility(def, sees, dist, optimal, hpFrac, toggled) {
    const inTarget = def.targetRange === 0 || dist <= def.targetRange;
    switch (def.botHint) {
        case 'gapClose':
            return sees && dist > optimal * 1.2 && inTarget;
        case 'escape':
            return sees && hpFrac < 0.35;
        case 'buff':
            return sees && dist <= optimal * 1.6;
        case 'aoe':
            return sees && dist <= (def.targetRange > 0 ? def.targetRange : 8);
        case 'deploy': {
            const first = def.steps[0]?.primitive;
            const barrier = first?.type === 'deploy' && first.kind === 'barrier';
            return sees && inTarget && (!barrier || hpFrac < 0.75);
        }
        case 'cc':
            return sees && dist <= (def.targetRange > 0 ? def.targetRange : 12);
        case 'heal':
            return hpFrac < 0.55;
        case 'stance':
            if (toggled)
                return dist < 8 || hpFrac < 0.3;
            return sees && dist <= optimal * 1.2 && dist > 6;
        case 'burst':
        case 'beam':
            return sees && inTarget;
        case 'cloak':
            return !sees || hpFrac < 0.5;
    }
}
function decide(rng, profile, me, def, weapon, bot, sees, dist, planar, hpFrac, heatFrac, enemyHpFrac, world, ctx, enemyCenter, enemyRadius, features) {
    const optimal = optimalRange(weapon);
    const known = bot.perceived !== null;
    const scores = {
        approach: known
            ? clamp((planar - optimal) / Math.max(optimal, 1), 0, 1.5) + (sees ? 0 : 0.6)
            : 1,
        strafe: sees && planar <= optimal * 1.4 ? 1 : 0.15,
        retreat: (hpFrac < 0.3 && enemyHpFrac > hpFrac ? 1.1 : 0) +
            (heatFrac > 0.85 ? 0.5 : 0) +
            (weapon.kind !== 'melee' && planar < optimal * 0.45 ? 0.6 : 0),
        cover: 0,
        hold: 0.1,
    };
    let cover = null;
    if (profile.useCover && sees && (hpFrac < 0.4 || me.overheatTicks > 0 || me.venting)) {
        cover = chooseCover(ctx.arena.cover, me.body.position, enemyCenter, enemyRadius, def.capsule.height * ctx.balance.aimHeightFraction, world);
        if (cover)
            scores.cover = 1.15;
    }
    // A nearby pickup the bot needs (shield gone, or running hot) is worth a detour: it walks
    // there through the cover mode's steering (Phase 8).
    const shieldFrac = def.shield > 0 ? me.shield / def.shield : 1;
    if (shieldFrac < 0.3 || heatFrac > 0.7) {
        const pickup = nearestUsefulPickup(ctx, features, me, shieldFrac < 0.3, heatFrac > 0.7);
        if (pickup) {
            cover = pickup;
            scores.cover = Math.max(scores.cover, 1.2);
        }
    }
    let mode = 'approach';
    let best = -1;
    for (const m of ['approach', 'strafe', 'retreat', 'cover', 'hold']) {
        if (scores[m] > best) {
            best = scores[m];
            mode = m;
        }
    }
    const abilityDef = getAbility(def.abilities[0].id);
    const ultDef = getAbility(def.ultimate);
    const ability = me.abilities[0].cooldown <= 0 &&
        me.body.energy >= abilityDef.energyCost &&
        wantsAbility(abilityDef, sees, dist, optimal, hpFrac, me.abilities[0].toggled) &&
        rngChance(rng, profile.abilityChance);
    const ultimate = me.ultCharge >= 100 &&
        wantsAbility(ultDef, sees, dist, optimal, hpFrac, me.abilities[1].toggled) &&
        rngChance(rng, profile.ultChance);
    return { mode, cover: mode === 'cover' ? cover : null, ability, ultimate };
}
const PICKUP_DETOUR = 28;
/** The closest available pickup of a kind the bot wants within the detour range, or null. */
function nearestUsefulPickup(ctx, features, me, wantShield, wantCooling) {
    let best = null;
    let bestDist = PICKUP_DETOUR;
    const pickups = ctx.arena.features.pickups;
    for (let i = 0; i < pickups.length; i++) {
        const p = pickups[i];
        const rt = features.pickups[i];
        if (!p || !rt || rt.respawnTick > 0)
            continue;
        if (p.kind === 'shieldCell' ? !wantShield : !wantCooling)
            continue;
        const d = Math.hypot(p.position[0] - me.body.position.x, p.position[1] - me.body.position.y, p.position[2] - me.body.position.z);
        if (d < bestDist) {
            bestDist = d;
            best = { x: p.position[0], y: p.position[1], z: p.position[2] };
        }
    }
    return best;
}
const HAZARD_LOOKAHEAD = 3.5;
/** Steers a planar move direction so the next few metres do not enter a hazard (Phase 8). */
function avoidHazards(features, origin, dir) {
    if (features.hazards.length === 0)
        return dir;
    const ahead = (d) => {
        const p = {
            x: origin.x + d.x * HAZARD_LOOKAHEAD,
            y: origin.y + 0.5,
            z: origin.z + d.z * HAZARD_LOOKAHEAD,
        };
        return features.hazards.some((h) => pointInYawBox(p, h.box));
    };
    if (!ahead(dir))
        return dir;
    const rot = (a) => ({
        x: dir.x * Math.cos(a) - dir.z * Math.sin(a),
        z: dir.x * Math.sin(a) + dir.z * Math.cos(a),
    });
    for (const deg of [70, -70, 120, -120]) {
        const alt = rot(degToRad(deg));
        if (!ahead(alt))
            return alt;
    }
    return { x: -dir.x, z: -dir.z };
}
/** Steers a planar move direction away from walls with three short rays. */
function avoidWalls(world, origin, dir) {
    const probe = 3.5;
    const clear = (d) => {
        const hit = world.castRayHit(origin, { x: d.x, y: 0, z: d.z }, probe);
        return hit ? hit.distance : probe;
    };
    if (clear(dir) >= probe)
        return dir;
    const rot = (a) => ({
        x: dir.x * Math.cos(a) - dir.z * Math.sin(a),
        z: dir.x * Math.sin(a) + dir.z * Math.cos(a),
    });
    const left = rot(degToRad(60));
    const right = rot(degToRad(-60));
    const l = clear(left);
    const r = clear(right);
    if (l >= r)
        return left;
    return right;
}
export const botDriver = (prev, slot, ctx, world, rng) => {
    const me = prev.mechs[slot];
    const bot = cloneBot(me.bot ?? createBotState('normal', me.body.position, me.body.yaw));
    const input = {
        ...IDLE_INPUT,
        seq: me.lastInput.seq + 1,
        aimYaw: bot.aimYaw,
        aimPitch: bot.aimPitch,
    };
    if (bot.level === 'idle')
        return { input, bot };
    const profile = BOT_PROFILES[bot.level];
    const def = ctx.defs[slot];
    const enemySlot = otherSlot(slot);
    const enemy = prev.mechs[enemySlot];
    const enemyDef = ctx.defs[enemySlot];
    const phase = prev.flow.phase;
    const live = phase === 'live' || phase === 'suddenDeath';
    const eye = aimOrigin(me.body, def, ctx.balance.aimHeightFraction);
    const enemyCenter = centerOf(enemy.body, enemyDef);
    const dx = enemyCenter.x - eye.x;
    const dy = enemyCenter.y - eye.y;
    const dz = enemyCenter.z - eye.z;
    const dist = Math.hypot(dx, dy, dz);
    const planar = Math.hypot(enemy.body.position.x - me.body.position.x, enemy.body.position.z - me.body.position.z);
    // --- perception ---
    const cloak = isCloaked(enemy);
    // A concealment zone (steam, dust) hides the enemy beyond its reveal distance (Phase 8).
    const concealed = concealmentAt(ctx.arena.features, enemyCenter);
    const sees = enemy.alive &&
        (!cloak.cloaked || dist <= cloak.revealDistance) &&
        (!concealed || dist <= concealed.revealDistance) &&
        lineOfSight(world, eye, enemyCenter, enemyDef.capsule.radius);
    if (sees) {
        bot.seenTicks++;
        const k = clamp(SIM_DT / Math.max(profile.reactionSeconds, SIM_DT), 0, 1);
        bot.perceived = bot.perceived ? vLerp(bot.perceived, enemyCenter, k) : { ...enemyCenter };
    }
    else {
        bot.seenTicks = 0;
    }
    bot.seen = sees;
    const aimAt = (point, lag, noise) => {
        const ax = point.x - eye.x;
        const ay = point.y - eye.y;
        const az = point.z - eye.z;
        const wantYaw = yawOf(ax, az) + (noise ? bot.noiseYaw : 0);
        const wantPitch = pitchOf(ax, ay, az) + (noise ? bot.noisePitch : 0);
        bot.aimYaw = wrapAngle(bot.aimYaw + wrapAngle(wantYaw - bot.aimYaw) * lag);
        bot.aimPitch = clamp(bot.aimPitch + (wantPitch - bot.aimPitch) * lag, -1.2, 1.2);
    };
    if (!me.alive || !live) {
        if (enemy.alive)
            aimAt(enemyCenter, 0.3, false);
        bot.decisionTicks = 0;
        bot.lastPosition = { ...me.body.position };
        input.aimYaw = bot.aimYaw;
        input.aimPitch = bot.aimPitch;
        return { input, bot };
    }
    const weapon = weaponOf(ctx, def.weapons[0].id);
    const hpFrac = me.hp / def.hp;
    const heatFrac = me.heat / def.heatCap;
    let useAbility = false;
    let useUlt = false;
    // --- decisions ---
    bot.decisionTicks--;
    if (bot.decisionTicks <= 0) {
        bot.decisionTicks =
            profile.decisionTicksMin +
                rngInt(rng, profile.decisionTicksMax - profile.decisionTicksMin + 1);
        bot.noiseYaw = rngBell(rng) * degToRad(profile.aimNoiseDeg);
        bot.noisePitch = rngBell(rng) * degToRad(profile.aimNoiseDeg) * 0.6;
        if (rngChance(rng, profile.strafeFlipChance))
            bot.strafeDir = bot.strafeDir === 1 ? -1 : 1;
        const d = decide(rng, profile, me, def, weapon, bot, sees, dist, planar, hpFrac, heatFrac, enemy.hp / enemyDef.hp, world, ctx, enemyCenter, enemyDef.capsule.radius, prev.features);
        bot.mode = d.mode;
        bot.coverTarget = d.cover;
        useAbility = d.ability;
        useUlt = d.ultimate;
    }
    // --- movement ---
    const optimal = optimalRange(weapon);
    const target = bot.perceived;
    let move = null;
    if (target) {
        const tx = target.x - me.body.position.x;
        const tz = target.z - me.body.position.z;
        const len = Math.hypot(tx, tz) || 1;
        const toward = { x: tx / len, z: tz / len };
        switch (bot.mode) {
            case 'approach':
                if (len > optimal * 0.9)
                    move = toward;
                break;
            case 'strafe': {
                const tangent = { x: -toward.z * bot.strafeDir, z: toward.x * bot.strafeDir };
                const radial = len > optimal * 1.1 ? 0.7 : len < optimal * 0.7 ? -0.7 : 0;
                const mx = tangent.x + toward.x * radial;
                const mz = tangent.z + toward.z * radial;
                const ml = Math.hypot(mx, mz) || 1;
                move = { x: mx / ml, z: mz / ml };
                break;
            }
            case 'retreat':
                move = { x: -toward.x, z: -toward.z };
                break;
            case 'cover':
                if (bot.coverTarget) {
                    const cx = bot.coverTarget.x - me.body.position.x;
                    const cz = bot.coverTarget.z - me.body.position.z;
                    const cl = Math.hypot(cx, cz);
                    if (cl > 1.2)
                        move = { x: cx / cl, z: cz / cl };
                }
                break;
            case 'hold':
                break;
        }
    }
    else if (bot.mode === 'approach') {
        // Never seen the enemy: walk toward the arena centre.
        const cx = -me.body.position.x;
        const cz = -me.body.position.z;
        const cl = Math.hypot(cx, cz);
        if (cl > 3)
            move = { x: cx / cl, z: cz / cl };
    }
    if (move) {
        move = avoidWalls(world, { x: me.body.position.x, y: me.body.position.y + 1.5, z: me.body.position.z }, move);
        move = avoidHazards(ctx.arena.features, me.body.position, move);
    }
    const moved = Math.hypot(me.body.position.x - bot.lastPosition.x, me.body.position.z - bot.lastPosition.z);
    if (move && moved < 0.03)
        bot.stuckTicks++;
    else
        bot.stuckTicks = 0;
    bot.lastPosition = { ...me.body.position };
    if (bot.stuckTicks > 20) {
        bot.jumpTicks = 5;
        bot.stuckTicks = 0;
        bot.strafeDir = bot.strafeDir === 1 ? -1 : 1;
    }
    if (bot.jumpTicks > 0) {
        input.jump = true;
        bot.jumpTicks--;
    }
    // --- aim (before converting movement to camera-relative axes) ---
    if (target) {
        let point = target;
        if (profile.lead && weapon.kind === 'projectile' && weapon.projectile && sees) {
            const tof = dist / weapon.projectile.speed;
            point = {
                x: target.x + enemy.body.velocity.x * tof,
                y: target.y + enemy.body.velocity.y * tof * 0.5,
                z: target.z + enemy.body.velocity.z * tof,
            };
        }
        // Close targets sweep across the aim quickly; tracking tightens with proximity.
        const lag = Math.min(0.9, profile.aimLag * (1 + 6 / Math.max(dist, 1)));
        aimAt(point, lag, true);
    }
    else {
        bot.aimYaw = wrapAngle(bot.aimYaw + 0.02);
        bot.aimPitch *= 0.95;
    }
    input.aimYaw = bot.aimYaw;
    input.aimPitch = bot.aimPitch;
    if (move) {
        const f = forwardXZ(bot.aimYaw);
        const r = rightXZ(bot.aimYaw);
        input.moveY = clamp(move.x * f.x + move.z * f.z, -1, 1);
        input.moveX = clamp(move.x * r.x + move.z * r.z, -1, 1);
        input.boost =
            (bot.mode === 'approach' || bot.mode === 'retreat' || bot.mode === 'cover') &&
                planar > 12 &&
                me.body.energy > 30;
    }
    // --- fire ---
    const reactionTicks = Math.round(profile.reactionSeconds / SIM_DT);
    const aimError = angleBetween(aimDirection(bot.aimYaw, bot.aimPitch), { x: dx, y: dy, z: dz });
    // The trigger opens when the aim error is within the gate or the target's own angular size.
    const angularRadius = Math.atan2(enemyDef.capsule.radius, Math.max(dist, 0.1));
    const gate = Math.max(degToRad(profile.fireAngleDeg), angularRadius);
    const ready = sees && bot.seenTicks >= reactionTicks;
    const steady = ready && aimError <= gate;
    const hot = heatFrac >= profile.heatCeiling || me.overheatTicks > 0;
    if (bot.holdFireTicks > 0)
        bot.holdFireTicks--;
    if (ready && !hot && bot.holdFireTicks === 0) {
        for (let i = 0; i < def.weapons.length; i++) {
            const w = weaponOf(ctx, def.weapons[i].id);
            const melee = w.kind === 'melee';
            const inRange = melee ? dist <= (w.melee?.reach ?? 3) + 0.8 : dist <= w.range.max;
            const tolerance = melee ? degToRad((w.melee?.arcDeg ?? 90) / 2) : gate;
            if (!inRange || aimError > tolerance)
                continue;
            if (def.weapons[i].slot === 'primary')
                input.primary = true;
            else if (dist > 8)
                input.secondary = true;
        }
    }
    if (weapon.kind !== 'melee' && planar <= 2.9)
        input.melee = true;
    input.vent = (heatFrac > 0.9 && !steady) || (me.overheatTicks > 0 && heatFrac > 0.4);
    if (useAbility)
        input.ability = true;
    if (useUlt)
        input.ultimate = true;
    return { input, bot };
};
//# sourceMappingURL=bot.js.map